import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import i18next from "i18next";

import { ErrorAlert, InfoAlert, SuccessAlert } from "@shared/ui/alerts";
import { AddDetail, generateInvoice } from "@modules/preinvoice";

import {
  createPreInvoice,
  createPreInvoiceLastMonth,
  validateHours,
  validateLastMonth,
} from "../api/adminApi";
import { HTTP_STATUS, SWAL_COLOR } from "../constants";

export interface PreInvoiceAdminProps {
  thisLoading: () => void;
  notLoading: () => void;
  userData: { id: number };
}

export const PreInvoiceAdmin = ({ thisLoading, notLoading, userData }: PreInvoiceAdminProps) => {
  const [addItemInvoiceDialog, setAddItemInvoiceDialog] = useState(false);
  const onActiveAddItemInvoiceDialog = () => setAddItemInvoiceDialog(true);
  const onDisabledAddItemInvoiceDialog = () => setAddItemInvoiceDialog(false);
  const urlAddItem = `admin/pre-invoice/add-item/${userData.id}`;
  const urlPreInvoicePending = `admin/pre-invoice/pending/${userData.id}`;
  const urlConfirmPreInvoice = `admin/pre-invoice/confirm/${userData.id}/`;
  const urlSendInvoice = `admin/invoice/send/${userData.id}`;

  const generatePreInvoice = () => {
    createPreInvoice(userData.id)
      .then((data) => {
        generateInvoice(
          data,
          thisLoading,
          notLoading,
          onActiveAddItemInvoiceDialog,
          urlPreInvoicePending,
          urlConfirmPreInvoice,
          urlSendInvoice
        );
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(
            i18next.t("errorToGeneratePreInvoiceTitle"),
            data?.message ?? i18next.t("internalError")
          ).then();
          return;
        }
        ErrorAlert("Error", i18next.t("errorToGeneratePreInvoice")).then();
      });
  };

  const generatePreInvoiceLastMonth = () => {
    thisLoading();
    createPreInvoiceLastMonth(userData.id)
      .then((data) => {
        notLoading();
        generateInvoice(
          data,
          thisLoading,
          notLoading,
          onActiveAddItemInvoiceDialog,
          urlPreInvoicePending,
          urlConfirmPreInvoice,
          urlSendInvoice
        );
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(
            i18next.t("errorToGeneratePreInvoiceTitle"),
            data?.message ?? i18next.t("internalError")
          ).then();
          return;
        }
        ErrorAlert("Error", i18next.t("errorToGeneratePreInvoice")).then();
      });
  };

  const validateHour = () => {
    validateHours(userData.id)
      .then(async (data) => {
        if (data.statusCode === HTTP_STATUS.SUCCESS) {
          await SuccessAlert(data.message as string).then();
        }
        if (data.statusCode === HTTP_STATUS.INFO) {
          await InfoAlert(data.message as string).then();
        }
        if (data.statusCode === HTTP_STATUS.BAD_REQUEST) {
          await ErrorAlert(data.message as string).then();
        }
        thisLoading();
        generatePreInvoice();
      })
      .catch(() => {
        ErrorAlert("Error", i18next.t("errorToValidateLoadHour")).then();
      });
  };

  const generatePreInvoiceInitial = () => {
    thisLoading();
    validateLastMonth(userData.id).then(async (data) => {
      notLoading();
      if (data.statusCode === HTTP_STATUS.SUCCESS) {
        Swal.fire({
          html: `${data.message} <br><br> ${i18next.t("generatePreInvoiceLastMonthQuestion")}`,
          title: i18next.t("preInvoiceLastMonthTitle"),
          icon: "info",
          showCancelButton: true,
          confirmButtonColor: SWAL_COLOR.CONFIRM,
          showDenyButton: true,
          cancelButtonColor: SWAL_COLOR.CANCEL,
          denyButtonColor: SWAL_COLOR.DENY,
          confirmButtonText: i18next.t("confirm"),
          denyButtonText: "No",
          cancelButtonText: i18next.t("cancel"),
        }).then(({ isConfirmed, isDenied }) => {
          if (isConfirmed) {
            generatePreInvoiceLastMonth();
          }
          if (isDenied) {
            validateHour();
          }
        });
      }
      if (data.statusCode === HTTP_STATUS.BAD_REQUEST) {
        validateHour();
      }
    });
  };

  useEffect(() => {
    generatePreInvoiceInitial();
  }, []);

  return (
    <AddDetail
      value={addItemInvoiceDialog}
      onDisabled={onDisabledAddItemInvoiceDialog}
      thisLoading={thisLoading}
      notLoading={notLoading}
      onActive={onActiveAddItemInvoiceDialog}
      urlAddItem={urlAddItem}
      urlPreInvoicePending={urlPreInvoicePending}
      onCollect={undefined}
      urlConfirmPreInvoice={urlConfirmPreInvoice}
      t={i18next.t}
    />
  );
};
